import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const AutoLogout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const events = [
    'load',
    'mousemove',
    'mousedown',
    'click',
    'scroll',
    'keypress',
    'touchstart',
    'touchmove',
    'keydown'
  ];

  const TIME_OUT = 4 * 60 * 1000; // 4 minutes
  const WARNING_TIME = 60 * 1000; // Show warning 1 minute before logout

  // Handle logout
  const handleLogout = useCallback(() => {
    // Clear timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    // Show logout message
    toast.info('You have been logged out due to inactivity', {
      position: 'top-center',
      autoClose: 3000,
    });

    // Perform logout
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Function to reset timer
  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (user) {
      // Set warning timer (show warning 1 minute before logout)
      warningTimerRef.current = setTimeout(() => {
        toast.warning('⚠️ You will be logged out in 1 minute due to inactivity', {
          position: 'top-center',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }, TIME_OUT - WARNING_TIME);

      // Set logout timer
      timerRef.current = setTimeout(() => {
        handleLogout();
      }, TIME_OUT);
    }
  }, [user, handleLogout]);

  // Set up event listeners
  useEffect(() => {
    if (!user) {
      return;
    }

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Start timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    };
  }, [user, resetTimer]);

  return children;
};

export default AutoLogout;
